import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import './style.css';

/**
 * ImageCarousel displays an infinite horizontal carousel of images with navigation arrows and auto-scroll.
 * @param {Object[]} images - Array of image objects ({ image, alt, description })
 * @param {number} autoScrollInterval - Time in ms between auto-scrolls (default: 3000)
 */
const ImageCarousel = ({ images, autoScrollInterval }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAuto, setIsAuto] = useState(!!autoScrollInterval);
    const autoScrollRef = useRef();
    const numImages = images.length;

    // Infinite scroll logic
    const getIndex = (idx) => (idx + numImages) % numImages;

    // Auto-scroll effect
    useEffect(() => {
        if (!isAuto || !autoScrollInterval) return;
        autoScrollRef.current = setInterval(() => {
            setCurrentIndex((prev) => getIndex(prev + 1));
        }, autoScrollInterval);
        return () => clearInterval(autoScrollRef.current);
    }, [isAuto, autoScrollInterval, numImages]);

    // Manual navigation disables auto-scroll
    const handlePrev = useCallback(() => {
        setCurrentIndex((prev) => getIndex(prev - 1));
        setIsAuto(false);
    }, [numImages]);
    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => getIndex(prev + 1));
        setIsAuto(false);
    }, [numImages]);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [handlePrev, handleNext]);

    // Show 1 main image, with optional previews (can be extended)
    return (
        <div className="image-carousel">
            <div className="image-carousel-track">
                {images.map((img, idx) => {
                    const pos = getIndex(idx - currentIndex);
                    if (pos === 0) {
                        return (
                            <div className="image-carousel-slide active" key={idx}>
                                <div className="image-carousel-caption">
                                    <div className="image-carousel-alt">{img.alt}</div>
                                    <div className="image-carousel-description text-align-center">{img.description}</div>
                                </div>
                                <div className="image-carousel-slide-image-container">
                                    <img src={CreateCloudfrontUrl(img.image)} alt={img.alt} className="image-carousel-img" />
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
            {/* --- Navigation Row: Arrows + Dots --- */}
            <div className="image-carousel-nav-row">
                <button className="image-carousel-arrow left" onClick={handlePrev} aria-label="Previous image">
                    <ChevronLeft />
                </button>
                <div className="image-carousel-dots">
                    {images.map((_, idx) => (
                        <button
                            key={idx}
                            className={`image-carousel-dot${currentIndex === idx ? ' image-carousel-dot--active' : ''}`}
                            aria-label={`Go to slide ${idx + 1}`}
                            onClick={() => {
                                setCurrentIndex(idx);
                                setIsAuto(false);
                            }}
                        />
                    ))}
                </div>
                <button className="image-carousel-arrow right" onClick={handleNext} aria-label="Next image">
                    <ChevronRight />
                </button>
            </div>
        </div>
    );
};

ImageCarousel.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            image: PropTypes.string.isRequired,
            alt: PropTypes.string.isRequired,
            description: PropTypes.string
        })
    ).isRequired,
    autoScrollInterval: PropTypes.number
};

export default ImageCarousel;
